import { z } from "zod";
import { actorIdentity, actorIdentitySchema, requireCapability, requireTenant, } from "./authority.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, evidenceBindingSchema, protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, verificationStatusSchema, } from "./identifiers.js";
export const reviewRequestSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    workItemId: protocolIdSchema,
    kind: z.enum(["HUMAN_CHECK", "APPEAL", "DISPUTE"]),
    binding: evidenceBindingSchema,
    receiptId: protocolIdSchema,
    requestedBy: actorIdentitySchema,
    requestedAt: protocolTimestampSchema,
    reason: z.string().trim().min(1),
    assignedAuthorityActorId: protocolIdSchema.nullable(),
    humanCheck: z
        .object({
        obligationId: protocolIdSchema,
        instruction: z.string().trim().min(1),
        evidenceArtifactIds: z.array(protocolIdSchema),
    })
        .strict()
        .nullable(),
    state: z.literal("OPEN"),
})
    .strict()
    .superRefine((value, context) => {
    if (value.kind === "HUMAN_CHECK" && value.humanCheck === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["humanCheck"],
            message: "human-check review requires a named check",
        });
    }
    if (value.kind !== "HUMAN_CHECK" && value.humanCheck !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["humanCheck"],
            message: "only human-check reviews may carry a named check",
        });
    }
});
export const reviewResolutionSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    reviewRequestId: protocolIdSchema,
    tenantId: protocolIdSchema,
    workItemId: protocolIdSchema,
    receiptId: protocolIdSchema,
    resolvedBy: actorIdentitySchema,
    resolvedAt: protocolTimestampSchema,
    outcome: z.enum([
        "NO_CHANGE",
        "NEW_RUN_REQUIRED",
        "SUCCESSOR_CONTRACT_REQUIRED",
        "OVERRIDE_REQUESTED",
    ]),
    humanDetermination: z
        .object({
        obligationId: protocolIdSchema,
        status: verificationStatusSchema,
        evidenceArtifactIds: z.array(protocolIdSchema),
        rationale: z.string().trim().min(1),
    })
        .strict()
        .nullable(),
    reason: z.string().trim().min(1),
})
    .strict();
export const humanReviewerCandidateSchema = z
    .object({
    actor: actorIdentitySchema,
    eligibleRoles: z.array(protocolIdSchema),
    capabilities: z.array(protocolIdSchema),
    evidenceGrantArtifactIds: z.array(protocolIdSchema),
    availableUntil: protocolTimestampSchema.nullable(),
    executionBoundaryId: protocolIdSchema,
})
    .strict();
export const humanOracleRoutingSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    reviewRequestId: protocolIdSchema,
    workKind: z.enum([
        "ux_quality",
        "research_synthesis",
        "policy_exception",
        "ambiguous_behavior",
    ]),
    status: z.enum(["ASSIGNED", "NOT_RUN"]),
    assignedAuthorityActorId: protocolIdSchema.nullable(),
    notRunReason: z
        .enum([
        "reviewer-unavailable",
        "missing-evidence-grant",
        "not-independent",
        "role-unavailable",
    ])
        .nullable(),
    nextAction: z.string().trim().min(1),
    reviewerSeesSourceArtifactIds: z.array(protocolIdSchema),
    deadline: protocolTimestampSchema,
    routedAt: protocolTimestampSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.status === "ASSIGNED" && value.assignedAuthorityActorId === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assignedAuthorityActorId"],
            message: "assigned human oracle routing requires a reviewer",
        });
    }
    if (value.status === "NOT_RUN" && value.notRunReason === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["notRunReason"],
            message: "NOT_RUN human oracle routing requires a reason",
        });
    }
});
function hasAll(actual, required) {
    const actualSet = new Set(actual);
    return required.every((item) => actualSet.has(item));
}
export function routeHumanOracleReview(input) {
    const request = reviewRequestSchema.parse(input.request);
    const candidates = input.candidates.map((candidate) => humanReviewerCandidateSchema.parse(candidate));
    const withRole = candidates.filter((candidate) => candidate.eligibleRoles.includes(input.requiredRole));
    const independent = withRole.filter((candidate) => candidate.actor.actorId !== input.workerActorId);
    const withGrant = independent.filter((candidate) => hasAll(candidate.evidenceGrantArtifactIds, input.requiredEvidenceArtifactIds));
    const available = withGrant.filter((candidate) => candidate.availableUntil !== null &&
        Date.parse(candidate.availableUntil) >= Date.parse(input.deadline));
    const assigned = available.find((candidate) => candidate.capabilities.includes("review:resolve") &&
        candidate.capabilities.includes("review:human-check"));
    if (assigned) {
        return humanOracleRoutingSchema.parse({
            protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
            id: input.id,
            reviewRequestId: request.id,
            workKind: input.workKind,
            status: "ASSIGNED",
            assignedAuthorityActorId: assigned.actor.actorId,
            notRunReason: null,
            nextAction: `Route ${input.workKind} review to ${assigned.actor.actorId}.`,
            reviewerSeesSourceArtifactIds: [...input.requiredEvidenceArtifactIds],
            deadline: input.deadline,
            routedAt: input.routedAt,
        });
    }
    const notRunReason = withRole.length === 0
        ? "role-unavailable"
        : independent.length === 0
            ? "not-independent"
            : withGrant.length === 0
                ? "missing-evidence-grant"
                : "reviewer-unavailable";
    return humanOracleRoutingSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        reviewRequestId: request.id,
        workKind: input.workKind,
        status: "NOT_RUN",
        assignedAuthorityActorId: null,
        notRunReason,
        nextAction: `Provide an independent ${input.requiredRole} reviewer with required evidence before the deadline.`,
        reviewerSeesSourceArtifactIds: [],
        deadline: input.deadline,
        routedAt: input.routedAt,
    });
}
export function createReviewRequest(input) {
    requireTenant(input.actor, input.binding.tenantId);
    requireCapability(input.actor, "review:request");
    return reviewRequestSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: input.binding.tenantId,
        workItemId: input.binding.workItemId,
        kind: input.kind,
        binding: input.binding,
        receiptId: input.receiptId,
        requestedBy: actorIdentity(input.actor),
        requestedAt: input.requestedAt,
        reason: input.reason,
        assignedAuthorityActorId: input.assignedAuthorityActorId ?? null,
        humanCheck: input.humanCheck ?? null,
        state: "OPEN",
    });
}
export function resolveReviewRequest(input) {
    const request = reviewRequestSchema.parse(input.request);
    requireTenant(input.actor, request.tenantId);
    requireCapability(input.actor, "review:resolve");
    if (input.actor.actorId === input.workerActorId) {
        throw new Error("a worker cannot resolve review work for its own attempt");
    }
    if (request.assignedAuthorityActorId !== null &&
        request.assignedAuthorityActorId !== input.actor.actorId) {
        throw new Error("review resolution requires the named authority");
    }
    if (request.kind === "HUMAN_CHECK") {
        requireCapability(input.actor, "review:human-check");
        if (!input.humanDetermination) {
            throw new Error("human-check resolution requires a determination");
        }
        // A determination is only evidence of the check that was actually asked for.
        // Without these two bindings a resolver may answer a different obligation,
        // or cite evidence the request never granted, and the resolution still
        // validates. Recovered from 685ec625a, discarded by merge 077d8a356.
        const humanCheck = request.humanCheck;
        if (!humanCheck) {
            throw new Error("human-check review requires a named check");
        }
        if (input.humanDetermination.obligationId !== humanCheck.obligationId) {
            throw new Error("human-check determination must target the requested obligation");
        }
        const requestedArtifactIds = new Set(humanCheck.evidenceArtifactIds);
        if (input.humanDetermination.evidenceArtifactIds.length === 0 ||
            input.humanDetermination.evidenceArtifactIds.some((artifactId) => !requestedArtifactIds.has(artifactId))) {
            throw new Error("human-check determination must cite only requested evidence artifacts");
        }
    }
    else if (input.humanDetermination) {
        throw new Error("only human-check reviews may record a human determination");
    }
    return reviewResolutionSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        reviewRequestId: request.id,
        tenantId: request.tenantId,
        workItemId: request.workItemId,
        receiptId: request.receiptId,
        resolvedBy: actorIdentity(input.actor),
        resolvedAt: input.resolvedAt,
        outcome: input.outcome,
        humanDetermination: input.humanDetermination ?? null,
        reason: input.reason,
    });
}
